if (!customElements.get('wlsn-product-form-frequency')) {
  class WLSNProductFormFrequency extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        inputSellingPlan: 'input[name="selling_plan"]',
        inputPurchaseType: 'input[name="purchase-type"]',
        subscriptionGroup: 'js-wlsn-product-form-frequency-subscription',
        oneTimeGroup: 'js-wlsn-product-form-frequency-one-time',
        subscriptionDiscount: 'js-subscription-discount',
        labelGroup: 'js-wlsn-product-form-frequency-label',
        optionsSelector: 'wlsn-options-selector',
        active: 'active',
        disabled: 'disabled',
        selected: 'selected',
      };

      this.purchaseType = {
        OTP: 'one-time',
        SUB: 'subscription',
      };

      this.currentSellingPlan = '';
    }

    get elements() {
      return {
        sellingPlanInput: this.querySelector(this.classes.inputSellingPlan),
        purchaseTypeInputs: this.querySelectorAll(this.classes.inputPurchaseType),
        subscriptionGroup: this.querySelector(`.${this.classes.subscriptionGroup}`),
        oneTimeGroup: this.querySelector(`.${this.classes.oneTimeGroup}`),
        optionsSelector: this.querySelector(this.classes.optionsSelector),
      };
    }

    connectedCallback() {
      this.bindEvents();

      // Emit initial purchase type on connect
      const checkedInput = this.querySelector('input[name="purchase-type"]:checked');
      if (checkedInput) {
        this.emitPurchaseTypeChange(checkedInput.value);
      }
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    bindEvents() {
      const { purchaseTypeInputs, optionsSelector, subscriptionGroup, oneTimeGroup } = this.elements;

      purchaseTypeInputs.forEach((input) => {
        input.addEventListener('change', this.handlePurchaseTypeChange.bind(this));
      });

      if (optionsSelector) {
        optionsSelector.addEventListener('wlsn:selector:change', this.handleOptionsSelectorChange.bind(this));
      }

      // Handle clicks on groups to allow child interactions
      if (subscriptionGroup) {
        subscriptionGroup.addEventListener('click', this.handleSubscriptionGroupClick.bind(this), true);
      }

      if (oneTimeGroup) {
        oneTimeGroup.addEventListener('click', this.handleOneTimeGroupClick.bind(this), true);
      }
    }

    unbindEvents() {
      const { purchaseTypeInputs, optionsSelector, subscriptionGroup, oneTimeGroup } = this.elements;

      purchaseTypeInputs.forEach((input) => {
        input.removeEventListener('change', this.handlePurchaseTypeChange.bind(this));
      });

      if (optionsSelector) {
        optionsSelector.removeEventListener('wlsn:selector:change', this.handleOptionsSelectorChange.bind(this));
      }

      if (subscriptionGroup) {
        subscriptionGroup.removeEventListener('click', this.handleSubscriptionGroupClick.bind(this), true);
      }

      if (oneTimeGroup) {
        oneTimeGroup.removeEventListener('click', this.handleOneTimeGroupClick.bind(this), true);
      }
    }

    handlePurchaseTypeChange(event) {
      const target = event.target;
      const value = target.value;
      const { purchaseTypeInputs } = this.elements;

      purchaseTypeInputs.forEach((input) => {
        input.checked = false;
      });
      target.checked = true;

      this.setSellingPlanInput(value);

      // Emit purchase type change event
      this.emitPurchaseTypeChange(value);
    }

    handleSubscriptionGroupClick(event) {
      const { optionsSelector } = this.elements;
      const clickedElement = event.target;

      // Check if click is on options selector or its children
      if (optionsSelector && optionsSelector.contains(clickedElement)) {
        // Let the options selector handle its own events
        return;
      }

      // Check if clicked on checkbox label - if so, let it work naturally
      if (clickedElement.closest(`.${this.classes.labelGroup}`)) {
        return;
      }

      // If click is anywhere else in the group, manually toggle the radio
      const subscriptionRadio = this.querySelector(`input[value="${this.purchaseType.SUB}"]`);
      if (subscriptionRadio && !subscriptionRadio.checked) {
        subscriptionRadio.checked = true;
        subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    handleOneTimeGroupClick(event) {
      const clickedElement = event.target;

      // Check if clicked on checkbox label - if so, let it work naturally
      if (clickedElement.closest(`.${this.classes.labelGroup}`)) {
        return;
      }

      // If click is anywhere else in the group, manually toggle the radio
      const oneTimeRadio = this.querySelector(`input[value="${this.purchaseType.OTP}"]`);
      if (oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    handleOptionsSelectorChange(event) {
      const { detail } = event;
      const { value } = detail;
      const { sellingPlanInput } = this.elements;

      // Update selling plan input value
      sellingPlanInput.value = value;
      this.currentSellingPlan = value;

      // Update subscription discount display
      this.updateSubscriptionDiscount();
    }

    setSellingPlanInput(value) {
      const { subscriptionGroup, oneTimeGroup, sellingPlanInput, optionsSelector } = this.elements;

      if (value === this.purchaseType.SUB) {
        let firstSellingPlanValue = '';

        if (optionsSelector) {
          // Get first option from options selector
          const firstOption = optionsSelector.querySelector('wlsn-options-selector-item.selected');
          firstSellingPlanValue = firstOption?.getAttribute('data-option-value') || '';

          optionsSelector.removeAttribute('disabled');
          optionsSelector.classList.remove(this.classes.disabled);
        }

        sellingPlanInput.value = firstSellingPlanValue;
        this.currentSellingPlan = firstSellingPlanValue;

        subscriptionGroup.classList.add(this.classes.active);
        oneTimeGroup.classList.remove(this.classes.active);

        this.updateSubscriptionDiscount();
      }

      if (value === this.purchaseType.OTP) {
        sellingPlanInput.value = '';
        this.currentSellingPlan = '';

        subscriptionGroup.classList.remove(this.classes.active);
        oneTimeGroup.classList.add(this.classes.active);

        if (optionsSelector) {
          optionsSelector.setAttribute('disabled', '');
          optionsSelector.classList.add(this.classes.disabled);
        }
      }
    }

    updateSubscriptionDiscount() {
      const discountElement = this.querySelector(`.${this.classes.subscriptionDiscount}`);
      if (!discountElement || !this.currentSellingPlan) return;

      const { optionsSelector } = this.elements;
      if (!optionsSelector) return;

      const selectedItem = optionsSelector.querySelector(
        `wlsn-options-selector-item[data-option-value="${this.currentSellingPlan}"]`
      );
      if (!selectedItem) return;

      const priceAdjustments = selectedItem.getAttribute('data-price-adjustments');
      if (!priceAdjustments) return;

      let adjustments = [];
      try {
        adjustments = JSON.parse(priceAdjustments);
      } catch (e) {
        console.warn('Invalid price adjustment JSON', e);
        return;
      }

      const adjustment = adjustments[0];
      if (!adjustment) return;

      if (adjustment.value_type === 'percentage') {
        discountElement.textContent = ` ${adjustment.value}%`;
      } else if (adjustment.value_type === 'amount') {
        discountElement.textContent = ` ${this.formatMoney(adjustment.value)}`;
      } else {
        discountElement.textContent = ``;
      }
    }

    formatMoney(cents) {
      const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
      return window.WLSNHelpers.formatMoney(cents, moneyFormat) || `$${(cents / 100).toFixed(2)}`;
    }

    /**
     * Emit purchase type change event
     * @param {string} purchaseType - The purchase type value
     * @returns {void}
     */
    emitPurchaseTypeChange(purchaseType) {
      window.WLSNHelpers.triggerEvent(this, 'wlsn:purchase-type:change', { purchaseType });
    }
  }

  customElements.define('wlsn-product-form-frequency', WLSNProductFormFrequency);
}
